import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Reveal } from "@/components/Reveal";
import { HelpCircle } from "lucide-react";

export const FAQS = [
  {
    q: "How do I enrol my child at Tutors Melbourne?",
    a: "To join our classes, simply complete the online enrolment form on our website. We run a hybrid model: online or in-person, so you can choose whichever suits your family.",
  },
  {
    q: "How are Tutors Melbourne classes structured?",
    a: "Classes are held weekly in small groups on a hybrid model: online or in-person. Each session introduces fresh content, includes collaborative work on problems, and steadily builds exam technique. All resources are uploaded to the Google Classroom so you can go back over the explanations at any time.",
  },
  {
    q: "What's the difference between group tutoring at Tutors Melbourne and hiring a private tutor?",
    a: "Private tutors often charge $100+ per hour for individual sessions. At Tutors Melbourne, a comparable investment covers a full week of well-structured classes, access to extensive learning resources, text-based support, practice exams, one-on-one consultations, and a digital classroom with all recordings and solutions. You're not just buying time — you're getting a complete, proven system built to help students achieve strong results.",
  },
  {
    q: "How does Tutors Melbourne compare to other tutoring services?",
    a: "Many tutoring providers depend heavily on the quality of individual tutors, which can make the experience quite variable. Tutors Melbourne takes a different approach. Our curriculum and teaching methods are consistent across classes, our tutors are carefully selected and supported, and our resources are developed in-house by our dedicated team. You're not taking a chance on a single tutor — you're joining a reliable, well-designed program built for consistent student success.",
  },
  {
    q: "What if I need help outside of regular class times?",
    a: "Quick questions can be sent via text support, which is available around the clock. For more detailed assistance, simply book a one-on-one consultation with your tutor. You also have full access to class recordings, worked solutions, and our complete resource library through the student portal. Support is always available when you need it — no waiting until the next class.",
  },
  {
    q: "Can I join partway through the year, or do I need to start from the beginning?",
    a: "You can join at any time. If you start mid-year, we'll create a personalised catch-up plan using our recorded classes, resources, and targeted consultations to help you get up to speed quickly. Our flexible approach meets students where they are and supports them as they progress.",
  },
  {
    q: "Are your classes designed only for weaker or stronger students?",
    a: "No — our classes are fully inclusive and designed to support students of all ability levels.",
  },
  {
    q: "What payment options are available?",
    a: "You can pay per class or purchase a term or bundle package for greater value.",
  },
];

export const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export const FaqSection = () => (
  <section className="py-20 px-6 bg-slate-50 dark:bg-[#0a0b10]" data-testid="faq-section">
    <div className="max-w-3xl mx-auto">
      <Reveal className="text-center mb-10">
        <Badge className="bg-brand-purple-soft text-brand-purple mb-4 font-body font-semibold px-3 py-1 rounded-full">
          <HelpCircle className="inline h-3.5 w-3.5 mr-1 -mt-0.5" /> FAQs
        </Badge>
        <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-3">
          Frequently asked questions
        </h2>
        <p className="text-slate-600 dark:text-slate-400 font-body text-base md:text-lg">
          Everything parents and students ask us before they join.
        </p>
      </Reveal>

      <Reveal delay={120}>
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className="bg-white dark:bg-[#1a1b23] border border-slate-100 dark:border-slate-800 rounded-2xl px-5 data-[state=open]:border-brand-purple/40"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger className="font-heading text-left text-base font-semibold text-slate-900 dark:text-white hover:no-underline py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-slate-600 dark:text-slate-300 font-body leading-relaxed pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

export default FaqSection;
